import React, {useEffect, useRef, useState} from 'react';
import {Link, useLocation, useNavigate} from "react-router-dom";
import axios from 'axios';
import {API_ENDPOINT} from '../config';
import {useAuth} from "../auth/AuthContext";

const RESEND_COOLDOWN = 60; // secunde, sincron cu throttle-ul din API

// Codul aplicatiei devine segment de package name Android (com.webnou.ro.multi.<code>),
// deci nu poate fi cuvant rezervat Java - build-ul ar pica abia in generator.
// Sincron cu RegistrationApiController::RESERVED_CODES.
const RESERVED_APP_CODES = [
    'abstract', 'assert', 'boolean', 'break', 'byte', 'case', 'catch', 'char',
    'class', 'const', 'continue', 'default', 'do', 'double', 'else', 'enum',
    'extends', 'final', 'finally', 'float', 'for', 'goto', 'if', 'implements',
    'import', 'instanceof', 'int', 'interface', 'long', 'native', 'new',
    'package', 'private', 'protected', 'public', 'return', 'short', 'static',
    'strictfp', 'super', 'switch', 'synchronized', 'this', 'throw', 'throws',
    'transient', 'try', 'void', 'volatile', 'while',
    'true', 'false', 'null',
];

const SignUpPage = ({bodyClass}) => {
    const location = useLocation();
    const confirmEmail = location.state?.confirmEmail || '';
    const [step, setStep] = useState(confirmEmail ? 'code' : 'form'); // 'form' | 'code'
    const [email, setEmail] = useState(confirmEmail);
    const [appCode, setAppCode] = useState('');
    const [ownerName, setOwnerName] = useState('');
    const [appName, setAppName] = useState('');
    const [password, setPassword] = useState('');
    const [password2, setPassword2] = useState('');
    const [code, setCode] = useState('');
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const navigate = useNavigate();
    const {setAuthData} = useAuth();

    const [altchaVerified, setAltchaVerified] = useState(false);
    const [altchaPayload, setAltchaPayload] = useState(null);
    const altchaRef = useRef(null);

    useEffect(() => {
        const previousClassName = document.body.className;
        document.body.className = bodyClass;
        return () => {
            document.body.className = previousClassName;
        };
    }, [bodyClass]);

    // venit din login (cont neconfirmat) -> retrimite automat un cod nou
    useEffect(() => {
        if (confirmEmail) {
            handleResend();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    useEffect(() => {
        const widget = altchaRef.current;

        if (!widget) {
            return;
        }

        const handleStateChange = (event) => {
            const state = event.detail?.state;

            if (state === 'verified') {
                setAltchaVerified(true);

                const input = widget.querySelector('input[name="altcha"]');
                setAltchaPayload(input?.value || null);
            } else {
                setAltchaVerified(false);
                setAltchaPayload(null);
            }
        };

        widget.addEventListener('statechange', handleStateChange);

        return () => {
            widget.removeEventListener('statechange', handleStateChange);
        };
    }, []);

    const apiError = (err, fallback) => {
        setError(err.response?.data?.error || fallback);
    };

    const handleRegister = async () => {
        setError(null);
        setInfo(null);

        if (!altchaVerified || !altchaPayload) {
            setError('Te rog să finalizezi verificarea CAPTCHA.');
            return;
        }
        if (password !== password2) {
            setError('Parolele nu coincid.');
            return;
        }
        if (!/^[a-z][a-z0-9]{2,20}$/.test(appCode)) {
            setError('Codul aplicației: doar litere mici și cifre, începe cu literă, 3-21 caractere.');
            return;
        }
        if (RESERVED_APP_CODES.includes(appCode)) {
            setError('Acest cod este rezervat și nu poate fi folosit ca identificator de aplicație. Alege altul.');
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API_ENDPOINT}/v1/register`, {email, password, code: appCode, name: ownerName, displayName: appName, altcha: altchaPayload});
            setStep('code');
            setCooldown(RESEND_COOLDOWN);
            setInfo('V-am trimis un cod de 6 cifre pe email.');
        } catch (err) {
            apiError(err, 'Eroare de conexiune la server. Încearcă din nou.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        setError(null);
        setInfo(null);
        setLoading(true);
        try {
            const response = await axios.post(`${API_ENDPOINT}/v1/register/confirm`, {email, code});
            const {token, roles} = response.data;
            setAuthData(token, roles);
            navigate('/', {replace: true});
        } catch (err) {
            apiError(err, 'Eroare de conexiune la server. Încearcă din nou.');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setError(null);
        setInfo(null);
        try {
            await axios.post(`${API_ENDPOINT}/v1/register/resend`, {email});
            setCooldown(RESEND_COOLDOWN);
            setInfo('Am retrimis codul pe email.');
        } catch (err) {
            apiError(err, 'Nu am putut retrimite codul. Încearcă din nou.');
        }
    };

    const appNameTrim = appName.trim();
    const appNameLen = appNameTrim.length;
    const appInitials = (appNameTrim.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('') || '?').toUpperCase();
    let nameColor = '#28a745', nameVerdict = 'încape complet';
    if (appNameLen > 20) { nameColor = '#dc3545'; nameVerdict = 'aproape sigur tăiat sub icon'; }
    else if (appNameLen > 12) { nameColor = '#d39e00'; nameVerdict = 'se poate trunchia'; }

    return (
        <div className="register-box">
            <div className="register-logo">
                <a href="https://www.webnou.ro">App.<b>WebNou</b>.Ro</a>
            </div>

            <div className="card">
                <div className="card-body register-card-body">
                    {step === 'form' ? (
                        <>
                            <p className="login-box-msg">Creează un cont nou</p>
                            {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
                            <form onSubmit={(e) => { e.preventDefault(); handleRegister(); }}>
                                <div className="input-group mb-3">
                                    <input type="text" className="form-control" placeholder="Numele tău sau al companiei" required
                                           value={ownerName} onChange={(e) => setOwnerName(e.target.value)}/>
                                    <div className="input-group-append">
                                        <div className="input-group-text"><span className="fas fa-user"></span></div>
                                    </div>
                                </div>
                                <div className="input-group mb-1">
                                    <input type="text" className="form-control" placeholder="Cod aplicație (ex: aquis)" required
                                           value={appCode}
                                           onChange={(e) => setAppCode(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                                           maxLength={21}/>
                                    <div className="input-group-append">
                                        <div className="input-group-text"><span className="fas fa-mobile-alt"></span></div>
                                    </div>
                                </div>
                                <small className="text-muted d-block mb-3">
                                    Identificatorul aplicației: litere mici și cifre, 3-21 caractere. Nu se mai poate schimba după generare.
                                </small>
                                <div className="input-group mb-1">
                                    <input type="text" className="form-control" placeholder="Nume aplicație (apare sub icon)"
                                           value={appName} onChange={(e) => setAppName(e.target.value)} maxLength={30}/>
                                    <div className="input-group-append">
                                        <div className="input-group-text"><span className="fas fa-font"></span></div>
                                    </div>
                                </div>
                                {appName && (
                                    <div className="d-flex align-items-center mb-1" style={{gap: '10px'}}>
                                        <div style={{width: '42px', height: '42px', borderRadius: '11px', background: '#534AB7', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', flex: 'none'}}>{appInitials}</div>
                                        <div style={{width: '72px', textAlign: 'center', fontSize: '11px', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{appName}</div>
                                        <small style={{color: nameColor}}>{appNameLen} caractere — {nameVerdict}</small>
                                    </div>
                                )}
                                <small className="text-muted d-block mb-3">
                                    Numele afișat sub iconul aplicației. Recomandat sub ~12 caractere ca să încapă complet. Opțional — îl poți schimba oricând.
                                </small>
                                <div className="input-group mb-3">
                                    <input type="email" className="form-control" placeholder="Email" required
                                           value={email} onChange={(e) => setEmail(e.target.value)}/>
                                    <div className="input-group-append">
                                        <div className="input-group-text"><span className="fas fa-envelope"></span></div>
                                    </div>
                                </div>
                                <div className="input-group mb-3">
                                    <input type="password" className="form-control" placeholder="Parolă (minim 6 caractere)"
                                           required minLength={6}
                                           value={password} onChange={(e) => setPassword(e.target.value)}/>
                                    <div className="input-group-append">
                                        <div className="input-group-text"><span className="fas fa-lock"></span></div>
                                    </div>
                                </div>
                                <div className="input-group mb-3">
                                    <input type="password" className="form-control" placeholder="Repetă parola" required
                                           value={password2} onChange={(e) => setPassword2(e.target.value)}/>
                                    <div className="input-group-append">
                                        <div className="input-group-text"><span className="fas fa-lock"></span></div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                                            {loading ? <i className="fas fa-spinner fa-spin"></i> : 'Înregistrare'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </>
                    ) : (
                        <>
                            <p className="login-box-msg">
                                Introduceți codul primit pe <b>{email}</b>
                            </p>
                            {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
                            {info && <div className="alert alert-success py-2" role="alert">{info}</div>}
                            <form onSubmit={(e) => { e.preventDefault(); handleConfirm(); }}>
                                <div className="input-group mb-3">
                                    <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                                           className="form-control text-center"
                                           style={{fontSize: '1.5rem', letterSpacing: '0.5rem'}}
                                           placeholder="______" required autoFocus
                                           value={code}
                                           onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}/>
                                </div>
                                <div className="row">
                                    <div className="col-12">
                                        <button type="submit" className="btn btn-primary btn-block"
                                                disabled={loading || code.length !== 6}>
                                            {loading ? <i className="fas fa-spinner fa-spin"></i> : 'Confirmă'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                            <p className="mt-3 mb-0 text-center">
                                <button type="button" className="btn btn-link p-0" disabled={cooldown > 0}
                                        onClick={handleResend}>
                                    {cooldown > 0 ? `Retrimite codul (${cooldown}s)` : 'Retrimite codul'}
                                </button>
                            </p>
                        </>
                    )}
                    <br/>
                    <p className="mb-0">
                        <altcha-widget
                            ref={altchaRef}
                            challenge={`${API_ENDPOINT}/captcha/challenge`}
                            name="altcha"
                        />
                    </p>
                    <p className="mt-3 mb-0">
                        <Link to="/login" className="text-center">Am deja cont</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SignUpPage;
